import type { FieldValues, UseFormSetError, FieldPath } from "react-hook-form";
import { getRecordString } from "./type-guards";

/**
 * Represents a Zod validation error issue
 */
interface ValidationIssue {
  code?: string;
  message: string;
  path: (string | number)[];
}

function isValidationIssue(value: unknown): value is ValidationIssue {
  if (typeof value !== "object" || value === null) return false;
  const maybe = value as { message?: unknown; path?: unknown };
  return (
    typeof maybe.message === "string" &&
    Array.isArray(maybe.path) &&
    maybe.path.every((p) => typeof p === "string" || typeof p === "number")
  );
}

function parseValidationIssuesFromUnknown(value: unknown): ValidationIssue[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const issues = value.filter(isValidationIssue);
  return issues.length > 0 ? issues : undefined;
}

function parseValidationIssuesFromString(value: string): ValidationIssue[] | undefined {
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) return undefined;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return parseValidationIssuesFromUnknown(parsed);
  } catch {
    return undefined;
  }
}

function parseJsonPointer(pointer: string): (string | number)[] {
  if (!pointer.startsWith("/")) return [pointer];
  return pointer
    .slice(1)
    .split("/")
    .map((segment) => {
      // RFC 6901 decoding
      const decoded = decodeURIComponent(segment.replace(/~1/g, "/").replace(/~0/g, "~"));
      const num = Number(decoded);
      // Return number if it's a valid integer, otherwise string
      return Number.isInteger(num) ? num : decoded;
    });
}

/**
 * Parse API error response into a structured format
 */
export function parseApiError(errorResponse: unknown): {
  message: string;
  validationIssues?: ValidationIssue[];
} {
  // Handle RFC 9457 Problem Details
  if (
    typeof errorResponse === "object" &&
    errorResponse !== null &&
    "type" in errorResponse &&
    "title" in errorResponse &&
    "status" in errorResponse
  ) {
    const problem = errorResponse as {
      type: string;
      title: string;
      status: number;
      detail?: string;
      errors?: Array<{ pointer: string; message: string }>;
    };

    let message = problem.detail || problem.title;
    let validationIssues: ValidationIssue[] | undefined;

    if (problem.errors && Array.isArray(problem.errors)) {
      validationIssues = problem.errors.map((err) => ({
        message: err.message,
        path: parseJsonPointer(err.pointer),
      }));

      // Update message if it's generic
      if ((message === "Validation failed" || message === "Bad Request") && validationIssues.length > 0) {
        message = validationIssues[0].message;
      } else if (validationIssues.length > 0) {
        message = `${message}: ${validationIssues[0].message}`;
      }
    }

    return {
      message,
      validationIssues,
    };
  }

  if (errorResponse instanceof Error) {
    const parsedFromErrorMessage = parseValidationIssuesFromString(errorResponse.message);
    if (parsedFromErrorMessage) {
      return {
        message: parsedFromErrorMessage[0]?.message || "Validation error",
        validationIssues: parsedFromErrorMessage,
      };
    }
    return { message: errorResponse.message || "An unexpected error occurred" };
  }

  // Handle array of validation issues (direct Zod format)
  const arrayIssues = parseValidationIssuesFromUnknown(errorResponse);
  if (arrayIssues) {
    return {
      message: arrayIssues[0]?.message || "Validation error",
      validationIssues: arrayIssues,
    };
  }

  // Handle wrapped format { error: string, details?: ValidationIssue[] }
  if (
    typeof errorResponse === "object" &&
    errorResponse !== null &&
    "error" in errorResponse
  ) {
    const wrapped = errorResponse as { error?: unknown; details?: unknown };
    const details = parseValidationIssuesFromUnknown(wrapped.details);
    const errorString = typeof wrapped.error === "string" ? wrapped.error : null;
    if (errorString) {
      const parsedIssuesFromError = parseValidationIssuesFromString(errorString);
      if (parsedIssuesFromError) {
        return {
          message: parsedIssuesFromError[0]?.message || "Validation error",
          validationIssues: parsedIssuesFromError,
        };
      }
      return {
        message: errorString,
        validationIssues: details,
      };
    }

    if (wrapped.error && typeof wrapped.error === "object") {
      const nestedMessage = getRecordString(wrapped.error, "message");
      if (nestedMessage) {
        const parsedIssuesFromNestedMessage = parseValidationIssuesFromString(nestedMessage);
        if (parsedIssuesFromNestedMessage) {
          return {
            message: parsedIssuesFromNestedMessage[0]?.message || "Validation error",
            validationIssues: parsedIssuesFromNestedMessage,
          };
        }
        return {
          message: nestedMessage,
          validationIssues: details,
        };
      }
      const nestedErrorString = getRecordString(wrapped.error, "error");
      if (nestedErrorString) {
        return {
          message: nestedErrorString,
          validationIssues: details,
        };
      }
    }
  }

  // Common shapes from thrown mutation / fetch errors
  const errorString = getRecordString(errorResponse, "error");
  if (errorString) return { message: errorString };

  const messageString = getRecordString(errorResponse, "message");
  if (messageString) return { message: messageString };

  // Handle simple string error
  if (typeof errorResponse === "string") {
    return { message: errorResponse };
  }

  // Default fallback
  return { message: "An unexpected error occurred" };
}

/**
 * Convert Zod error path array to react-hook-form field path
 * Handles paths like ["body", "bookingClosesAt"] -> "bookingClosesAt"
 * or ["body", "sessions", 0, "startsAt"] -> "sessions.0.startsAt"
 */
function convertPathToFieldPath(path: (string | number)[]): string {
  // Remove "body" prefix if present (from chanfana validation errors)
  const cleanPath = path[0] === "body" ? path.slice(1) : path;

  return cleanPath.join(".");
}

/**
 * Apply validation errors to react-hook-form
 */
function applyValidationErrors<T extends FieldValues>(
  validationIssues: ValidationIssue[],
  setError: UseFormSetError<T>,
): void {
  for (const issue of validationIssues) {
    const fieldPath = convertPathToFieldPath(issue.path) as FieldPath<T>;
    if (fieldPath) {
      setError(fieldPath, {
        type: "server",
        message: issue.message,
      });
    }
  }
}

/**
 * Handle API error response and apply validation errors to form if applicable
 * Returns the error message for displaying in toast and whether validation errors were found
 */
export function handleApiError<T extends FieldValues>(
  errorResponse: unknown,
  setError?: UseFormSetError<T>,
): { message: string; hasValidationErrors: boolean } {
  const { message, validationIssues } = parseApiError(errorResponse);
  const hasValidationErrors = validationIssues !== undefined && validationIssues.length > 0;

  // Apply validation errors to form fields if setError is provided
  if (hasValidationErrors && setError && validationIssues) {
    applyValidationErrors(validationIssues, setError);

    // Return the first validation error message for the toast
    return {
      message:
        validationIssues[0]?.message || "Please fix the validation errors in the form",
      hasValidationErrors: true,
    };
  }

  return { message, hasValidationErrors: false };
}
